import { ComplexTable } from '@synergy/table';
import { Col, notification, Row, Select } from 'antd';
import clsx from 'clsx';
import i18next from "i18next";
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../../../contexts/AuthContext';
import query from '../../../../../server/constants.api';
import useFetch from "../../../../../server/useFetch";
import { PageContent, PageSection, PageTitle } from "../../../../../styles/global/Pages.styled";
import { booleans, period, periodicity } from '../../../../../utils/config/widgetsConfig';
import Spinner from '../../../Atomics/Spinner';
import { Text } from '../../../Atomics/Text';
import permissionToEdit from '../AccessUtils';
import { buildObj, getCustomizedButtonsForConfiguration } from '../ConfigurationUtils';
import { EditPopupConfigurations } from '../EditPopupConfigurations';

export const MetricsConfigurations = ({ ...props }) => {
    // Obtém a função de tradução do hook useTranslation para uso na tradução de textos
    const allowedToWrite = permissionToEdit(['Configurations', 'Metrics', 'MetricsConfigurations'])
    const { t } = useTranslation();
    const { tenant } = useAuthContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [selectedTenant, setSelectedTenant] = useState(null);

    const [isTofecth, setIsToFetch] = useState(false);
    const [bodyToPost, setBodyToPost] = useState(null);
    const [isToEdit, setIsToEdit] = useState(false);

    const [isToFetchModalConfigs, setIsToFetchModalConfigs] = useState(false);

    const [idToDelete, setIdToDelete] = useState(null);
    const [isToFetchDelete, setIsToFetchDelete] = useState(false);

    const {
        data: units,
    } = useFetch(`/configurations/metric/tenant/${selectedTenant}`, "getConfigsByTenant", null, isToFetchModalConfigs, null, query.middlewareURL);

    useEffect(() => {
        setIsToFetchModalConfigs(false);
    }, [units])

    const {
        data: metrics,
        refetch: refetchMetrics,
    } = useFetch("/configurations/metric", "metricConfigurations", { tenant: tenant }, null, null, query.middlewareURL);

    const {
        data: configs,
    } = useFetch("/configurations/metric/modal", "metricModalConfigs", { tenant: tenant }, null, null, query.middlewareURL);

    const {
        data: deleted,
    } = useFetch("/configurations/metric", "deleteMetricConfig", { id: idToDelete }, isToFetchDelete, "DELETE", query.middlewareURL);

    useEffect(() => {
        setIsToFetchDelete(false);
        refetchMetrics();
    }, [deleted])

    const {
        data: addOrEditMetrics,
        isFetching: addOrEditMetricsIsFetching,
        error: addOrEditMetricsError,
        isSuccess: addOrEditMetricsIsSuccess,
        isError: addOrEditMetricsIsError
    } = useFetch("/configurations/metric", "saveOrEditMetricConfig", bodyToPost, isTofecth, isToEdit ? 'PUT' : 'POST', query.middlewareURL);

    useEffect(() => {
        if (addOrEditMetricsIsError) {
            const errorMessage = addOrEditMetricsError?.response?.data?.detailedMessage || t('An error occurred while saving the metric.');

            // Usar uma notificação para exibir o erro
            notification.error({
                message: t('Error'),
                description: t(errorMessage),
                duration: 3,  // Define a duração da notificação (3 segundos)
            });
        } else if (addOrEditMetrics && !addOrEditMetricsIsFetching && addOrEditMetricsIsSuccess) {
            refetchMetrics();
            setSelectedRecord(null);
        }
        setIsToFetch(false);
    }, [addOrEditMetricsIsFetching, addOrEditMetricsIsError]);

    const mathFunctions = [
        "abs", "acos", "acosh", "asin", "asinh", "atan", "atan2", "atanh",
        "cbrt", "ceil", "cos", "cosh", "exp", "expm1", "floor", "fround",
        "log", "log1p", "log10", "log2", "max", "min", "pow", "round",
        "sign", "sin", "sinh", "sqrt", "tan", "tanh", "trunc"
    ];

    const tableConfiguration = [
        {
            title: t('Tenant'),
            dataIndex: 'tenant',
        },
        {
            title: t('Class'),
            dataIndex: 'class',
        },
        {
            title: t('Source Table'),
            dataIndex: 'source',
        },
        {
            title: t('Source Metric Label'),
            dataIndex: 'sourceMetricLabel',
        },
        {
            title: t('Periodicity'),
            dataIndex: 'periodicity',
        },
        {
            title: t('Operations'),
            dataIndex: 'operations',
        },
        {
            title: t('Operation Horizon'),
            dataIndex: 'operationHorizon',
        },
        {
            title: t('Expression'),
            dataIndex: 'expression',
        },
        {
            title: t('Destination Metric Label'),
            dataIndex: 'destinationMetricLabel',
        },
        {
            title: t('Destination Metric Label Unit'),
            dataIndex: 'destinationMetricLabelUnit',
        },
    ];

    const modalConfiguration = [
        {
            title: t('Tenant'),
            dataIndex: 'tenant',
            fieldTypeId: 6, // https://u1g9qpyluh.execute-api.eu-west-1.amazonaws.com/v3_0_01/graph/items?tenant=<TOP_LEVEL_CLIENT_TENANT>
            description: t('Indicates the source of the data, which will be used to do any operation.'),
            required: true,
            customObject: (form) => {
                return (
                    <Select
                        listHeight={200}
                        size={'large'}
                        bordered={false}
                        options={returnOptions('tenant', configs)}
                        onChange={(e, record) => { form.setFieldsValue({ class: null, source: null, sourceMetricLabel: null }); setSelectedTenant(e); setIsToFetchModalConfigs(true); }}
                        className={clsx('select-atomic', props?.className, 'full-width')} />
                )
            }
        },
        {
            title: t('Class'),
            dataIndex: 'class',
            fieldTypeId: 6, // https://u1g9qpyluh.execute-api.eu-west-1.amazonaws.com/v3_0_01/metrics/classes?tenant_base=<TENANT_TOP_LEVEL>&tenant=<TENANT>
            description: t('Selection of all federated classes, i.e., all classes that are currently capable of receiving data. If any node is federated, it will be capable of receiving data.'),
            required: true,
            customObject: (form) => {
                return (
                    <Select
                        listHeight={200}
                        size={'large'}
                        disabled={form.getFieldValue("class") || units ? false : true}
                        bordered={false}
                        options={returnOptions('metrics_classes', units)}
                        className={clsx('select-atomic', props?.className, 'full-width')} />
                )
            }
        },
        {
            title: t('Source Table'),
            dataIndex: 'source',
            fieldTypeId: 6, // https://u1g9qpyluh.execute-api.eu-west-1.amazonaws.com/v3_0_01/metrics/tables?tenant_base=<TENANT_TOP_LEVEL>&tenant=<TENANT>
            description: t('Selection of all possible data tables, i.e., all tables that <tenant>  has access, that stores or will store data.'),
            required: true,
            customObject: (form) => {
                return (
                    <Select
                        listHeight={200}
                        size={'large'}
                        bordered={false}
                        disabled={form.getFieldValue("source") || units ? false : true}
                        options={returnOptions('metrics_tables', units)}
                        className={clsx('select-atomic', props?.className, 'full-width')} />
                )
            }
        },
        {
            title: t('Source Metric Label'),
            dataIndex: 'sourceMetricLabel', // https://u1g9qpyluh.execute-api.eu-west-1.amazonaws.com/v3_0_01/tenants_configs/selected_metrics?tenant_base=<TENANT_TOP_LEVEL>&tenant=<TENANT>
            fieldTypeId: 6,
            description: t('Selection of all possible metrics, i.e., all metrics that <tenant>  has access. Note: It\'s possible to not have data for this metric, if the metric is only added and the data is not sent.'),
            required: true,
            customObject: (form) => {
                return (
                    <Select
                        listHeight={200}
                        size={'large'}
                        bordered={false}
                        disabled={form.getFieldValue("sourceMetricLabel") || units ? false : true}
                        options={returnOptions('tenants_configs', units)}
                        className={clsx('select-atomic', props?.className, 'full-width')} />
                )
            }
        },
        {
            title: t('Periodicity'),
            dataIndex: 'periodicity',
            fieldTypeId: 3,
            description: t('This dropdown menu allows the selection of a specific data periodicity: hourly, daily, or monthly, i.e. the data will be aggregated by the specified period.'),
            required: true,
        },
        {
            title: t('Operation'),
            dataIndex: 'operations', // https://u1g9qpyluh.execute-api.eu-west-1.amazonaws.com/v3_0_01/metrics/operations
            fieldTypeId: 3,
            description: t('These fields together indicates the transformation to apply to the data (Operation + Expression) and the frequency of the data processment (Operation Horizon)'),
            required: true,
            size: 8
        },
        {
            title: t('Operation Horizon'),
            dataIndex: 'operationHorizon',
            fieldTypeId: 3,
            description: t('These fields together indicates the transformation to apply to the data (Operation + Expression) and the frequency of the data processment (Operation Horizon)'),
            required: true,
            size: 8
        },
        {
            title: t('Expression'),
            dataIndex: 'expression',
            description: t('Math Expression with the available functions:') + " " + mathFunctions.map(e => ' ' + e) + " " + t("Example of valid Expression: x+2"),
            required: true,
            size: 8,
            validateRule: (_, value, setFieldStatus) => {
                try {
                    if (!value || !value.includes("x")) {
                        setFieldStatus({
                            validateStatus: 'error',
                            help: 'Expression does not contain x variable.',
                        });
                        return Promise.reject("Expression does not contain x variable")
                    }
                    let num = 5;
                    mathFunctions.forEach(func => {
                        const regex = new RegExp(`\\b${func}\\(`, 'g');
                        value = value.replace(regex, `Math.${func}(`);
                    });
                    let res = eval?.(`"use strict";(${value.replace(/x/g, num)})`);
                    if (res != 'Infinity') {
                        setFieldStatus({
                            validateStatus: 'success',
                            help: <span style={{ color: 'limegreen' }}>{t('Success! With ') + num + ' ' + t('the result was: ') + res.toFixed(2)}</span>,
                        });
                        return Promise.resolve()
                    }
                    else {
                        setFieldStatus({
                            validateStatus: 'error',
                            help: 'Error in Expression.',
                        });
                        return Promise.reject("Error in Expression")
                    }
                } catch (error) {
                    setFieldStatus({
                        validateStatus: 'error',
                        help: 'Error in Expression.',
                    });
                    return Promise.reject("Error in Expression")
                }
            }
        },

        {
            title: t('Destination Metric Label'),
            dataIndex: 'destinationMetricLabel',
            required: true,
            description: t('Indicates the output label of the processed metric.'),
        },
        {
            title: t('Destination Metric Label Unit'),
            dataIndex: 'destinationMetricLabelUnit',
            required: true,
            description: t('Indicates the output unit of the processed metric.'),
        },
    ];

    function handleOkModal(data) {
        if (data && selectedRecord) {
            const bodyToPost = {
                "ID": selectedRecord.id,
                "Tenant": selectedTenant ?? data?.tenant,
                "Class": data?.class,
                "Source": data?.source,
                "Metric_Label_Source": data?.sourceMetricLabel,
                "Periodicity": data?.periodicity,
                "Operation": data?.operations,
                "Operation_Horizon": data?.operationHorizon,
                "Expression": data?.expression,
                "Metric_Label_Destiny": data?.destinationMetricLabel,
                "Metric_Label_Destiny_Unit": data?.destinationMetricLabelUnit,
            };
            setBodyToPost(bodyToPost);
            setIsToFetch(true);
            setIsModalOpen(false);
            setSelectedRecord(null);
            refetchMetrics();
        }
    }

    function handleEdit(record) {
        setIsToEdit(true); // Ativa a flag de edição
        setSelectedTenant(record?.tenant)
        setIsToFetchModalConfigs(true);
        setSelectedRecord(record);  // Atualiza o estado com o registro selecionado
        setIsModalOpen(true); // Abre a modal
    }

    function handleCloseModal() {
        setIsModalOpen(false);  // Fecha a modal
        setSelectedRecord(null); // Limpa o registro selecionado
    }

    return (
        <PageContent className={clsx('building_room_details', props?.className)}>
            <PageSection>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Row gutter={[16, 16]}>
                            <Col span={4}>
                                <PageTitle>
                                    <Text $fontColor='coolgray-12' $fontSize='text_md' $weight='medium' $marginRight='s03'>{t('Metric List')}</Text>
                                </PageTitle>
                            </Col>
                        </Row>
                    </Col>
                    <Col span={24}>
                        {
                            metrics && configs ?
                                <ComplexTable fetchData={serealizeData(metrics)} language={i18next.resolvedLanguage}
                                    tableConfiguration={{ collumnsConfiguration: tableConfiguration }}
                                    editButton={allowedToWrite}
                                    deleteButton={allowedToWrite}
                                    fixedActions={true}
                                    handleDelete={(record) => { setIdToDelete(record.id); setIsToFetchDelete(true); }}
                                    pagination={{ pageSize: 5 }}
                                    handleEdit={(record) => { handleEdit({ ...emptyRecord, ...record }) }}
                                    customizedButtons={
                                        getCustomizedButtonsForConfiguration(metrics && configs && allowedToWrite,
                                            () => { setIsToEdit(false); setSelectedRecord(emptyRecord); setIsModalOpen(true) }, t)
                                    }
                                />
                                :
                                <div style={{ height: 'inherit', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                    <Spinner stroke="green" />
                                </div>
                        }
                    </Col>
                    <EditPopupConfigurations
                        openModal={isModalOpen}
                        closeModal={handleCloseModal}
                        onOkModal={(data) => handleOkModal(data)}
                        selectedRecord={selectedRecord ? serealizeRecordForModal(modalConfiguration, selectedRecord, configs) : null}
                        isToEdit={isToEdit}
                        customWidth={800} />
                </Row>
            </PageSection>
        </PageContent>
    )
}

//Serializer para tornar a modal no formato correto para editar um User, como uma configuração para a Modal
const serealizeRecordForModal = (modalConfiguration, selectedRecord, configValues) => {
    const keysToKeep = modalConfiguration.flatMap(x => {
        if (x.subfields) {
            return [...x.subfields.map(y => y.dataIndex), x.dataIndex];
        } else {
            return x.dataIndex
        }
    });
    const result = modalConfiguration.flatMap(x => {
        var obj = Object.entries(selectedRecord);
        if (x.subfields) {
            var foundObj = obj.find(z => z[0] === x.dataIndex);
            var parentObj = buildObj(foundObj, x, null, true, returnOptions, configValues);
            return [({
                childs: x.subfields.map(y => {
                    var foundObj = obj.find(z => z[0] === y.dataIndex);
                    return buildObj(foundObj, y, true, parentObj.key, returnOptions, configValues);
                }), parent: parentObj
            })];
        } else {
            var foundObj = obj.find(z => z[0] === x.dataIndex);
            return buildObj(foundObj, x, null, null, returnOptions, configValues);
        }
    })

    return result.filter(x => {
        if (x) {
            if (x?.parent) {
                for (var i = 0; i < x.childs.length; i++) {
                    if (!x.childs[i] || !keysToKeep.includes(x.childs[i].key))
                        delete x.childs[i]
                }
                return keysToKeep.includes(x.parent.key)
            }
            return keysToKeep.includes(x.key)
        }
    });
};

const emptyRecord = {
    'id': null,
    'tenant': null,
    'class': null,
    'source': null,
    'sourceMetricLabel': null,
    'periodicity': null,
    'operations': null,
    'operationHorizon': null,
    'expression': null,
    'destinationMetricLabel': null,
    'destinationMetricLabelUnit': null,
}

function uniqueObjectsByProperty(array, property) {
    if (property)
        return Array.from(new Set(array.map(item => item[property]))).map(value => ({ label: value, value: value }));
    else
        return Array.from(new Set(array)).map(value => ({ label: value, value: value }));
}

function returnOptions(key, configValues) {
    switch (key) {
        case 'tenant':
            if (configValues) {
                var x = uniqueObjectsByProperty(configValues[key].result, key)
                x.push({ lable: "TOP/DELOITTE", value: "TOP/DELOITTE" })
                return x
            }
        case 'periodicity':
            return periodicity.map(x => { return { lable: x, value: x } });
        case 'period':
            return period.map(x => { return { lable: x, value: x } });
        case 'operationHorizon':
            return period.map(x => { return { lable: x.toLocaleLowerCase(), value: x.toLocaleLowerCase() } });
        case 'detailSeperation':
        case 'period':
        case 'history':
            return booleans.map(x => { return { label: x, value: x } });
        default:
            if (configValues && configValues[key]) {
                if (configValues[key].result) {
                    var x = uniqueObjectsByProperty(configValues[key].result.map(x => { return { label: x, value: x } }), key);
                    return x;
                } else if (Array.isArray(configValues[key])) {
                    var x = uniqueObjectsByProperty(configValues[key]);
                    return x;
                }
            } else if (configValues?.key) {
                var x = configValues.key.map(x => { return { label: x, value: x } });
                return x;
            }
            else
                return [];
    }
}

const serealizeData = (values) => {
    return values?.map(e => {
        return {
            id: e['ID'],
            tenant: e["Tenant"],
            class: e["Class"],
            source: e["Source"],
            sourceMetricLabel: e["Metric_Label_Source"],
            periodicity: e["Periodicity"],
            operations: e["Operation"],
            operationHorizon: e["Operation_Horizon"],
            expression: e["Expression"] ?? "x+2",
            destinationMetricLabel: e["Metric_Label_Destiny"],
            destinationMetricLabelUnit: e["Metric_Label_Destiny_Unit"],
        }
    })
}